import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Filter } from "lucide-react";

const CATS = ["All", "Furniture", "Electronics", "Clothing & Fashion", "Kitchen", "Baby & Kids", "Books", "Cultural Goods", "Beauty & Wellness", "Vehicles", "Free / Giveaway", "Other"];

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const q = params.get("q") || "";
  const category = params.get("category") || "All";
  const sort = params.get("sort") || "recent";
  const isFree = params.get("is_free");

  useEffect(() => {
    setLoading(true);
    const query = { q, sort, limit: 48 };
    if (category && category !== "All") query.category = category;
    if (isFree === "true") query.is_free = true;
    api.get("/listings", { params: query })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [q, category, sort, isFree]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v == null || v === "" || v === "All") p.delete(k); else p.set(k, v);
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">Marketplace</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#1E3A2F] mt-1">Community goods & gifts</h1>
          <p className="text-[#5C5C5C] mt-2 text-sm">{total} listing{total === 1 ? "" : "s"} across Washington State</p>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E2DED0] p-4 sm:p-5 mb-8 grid md:grid-cols-[1fr_auto_auto_auto] gap-3 items-center">
        <div className="flex items-center gap-2 bg-[#F7F4EB] rounded-full px-3">
          <Search className="w-4 h-4 text-[#5C5C5C]" />
          <Input
            data-testid="market-search-input"
            defaultValue={q}
            onKeyDown={(e) => { if (e.key === "Enter") setParam("q", e.currentTarget.value); }}
            placeholder="Search listings…"
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <Select value={category} onValueChange={(v) => setParam("category", v)}>
          <SelectTrigger data-testid="market-category-select" className="min-w-[180px]"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
          <SelectTrigger data-testid="market-sort-select" className="min-w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Most recent</SelectItem>
            <SelectItem value="price_asc">Price: Low to High</SelectItem>
            <SelectItem value="price_desc">Price: High to Low</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={isFree === "true" ? "default" : "outline"}
          className={isFree === "true" ? "abm-btn-pine rounded-full" : "rounded-full"}
          onClick={() => setParam("is_free", isFree === "true" ? "" : "true")}
          data-testid="market-free-toggle"
        >
          <Filter className="w-4 h-4 mr-1" /> Free only
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#5C5C5C]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E2DED0] rounded-2xl bg-white/60">
          <div className="font-display text-2xl text-[#1E3A2F]">No listings match your filters</div>
          <p className="text-sm text-[#5C5C5C] mt-2">Try a different category or clear the search.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      )}
    </div>
  );
}
